'use client';

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { usePageMenu } from '../../hooks/usePageMenu';
import { Link } from 'gatsby';
import { getLink } from '../../utils/link';
import CaretIcon from '../atoms/icons/caret';

const PageMenu = ({ blok, currentStoryInternalId }) => {
  const links = usePageMenu(blok);
  const linkRefs = useRef([]);
  const [overflowMarker, setOverflowMarker] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);

  const buttonStyles =
    'flex no-underline items-center mx-1 rounded-full border transition-border duration-300 ease-in-out font-crystalCondensed';

  const linkTitle = (link) => {
    const linkTitle =
      link.node.lang === 'default'
        ? link.node.name
        : link.node.translated_slugs.find(
            (langObj) => langObj.lang === link.node.lang,
          ).name ||
          link.node.name ||
          'No translation';

    return linkTitle;
  };

  const scrollToActiveLink = useCallback(() => {
    if (userScrolled) return;

    if (linkRefs.current.length > 0) {
      const activeLinkRef = linkRefs.current.find(
        (ref) => ref && ref.dataset.id === currentStoryInternalId.toString(),
      );

      if (activeLinkRef) {
        const parentElement = activeLinkRef.parentElement;
        const parentElementWidth = parentElement.offsetWidth;
        const activeLinkRefWidth = activeLinkRef.offsetWidth;
        const activeLinkRefOffsetLeft = activeLinkRef.offsetLeft;

        const scrollLeft =
          activeLinkRefOffsetLeft -
          (parentElementWidth - activeLinkRefWidth) / 2;

        parentElement.scrollLeft = scrollLeft;
      } else {
        console.log('Active link not found!');
      }
    } else {
      console.log('Refs array is empty!');
    }
  }, [currentStoryInternalId, userScrolled]);

  const handleScroll = useCallback(() => {
    const parentElement = linkRefs.current[0]?.parentElement;

    if (!parentElement) return;

    setIsScrolling(true);
    setUserScrolled(true);

    // Use a timeout to determine when the user has stopped scrolling
    const timeoutId = setTimeout(() => {
      setIsScrolling(false);
    }, 200);

    const isAtStart = parentElement.scrollLeft === 0;
    const isAtEnd =
      parentElement.scrollLeft + parentElement.offsetWidth >=
      parentElement.scrollWidth;

    // Determine overflow state
    if (isAtStart && !isAtEnd) {
      setOverflowMarker('right');
    } else if (!isAtStart && isAtEnd) {
      setOverflowMarker('left');
    } else if (!isAtStart && !isAtEnd) {
      setOverflowMarker('both');
    } else if (isAtStart && isAtEnd) {
      setOverflowMarker(null);
    }

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    if (!isScrolling) {
      scrollToActiveLink();
    }
  }, [scrollToActiveLink, links, isScrolling]);

  useEffect(() => {
    const handleResize = () => {
      scrollToActiveLink();
      handleScroll();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollToActiveLink, handleScroll]);

  useEffect(() => {
    if (linkRefs.current.length > 0) {
      const parentElement = linkRefs.current[0]?.parentElement;

      if (!overflowMarker) {
        handleScroll();
      }

      if (parentElement) {
        parentElement.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (parentElement) {
          parentElement.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [handleScroll, overflowMarker]);

  return (
    <div
      {...storyblokEditable(blok)}
      className="col-span-12 flex items-center mt-8 px-0 md:px-4 overflow-x-auto no-scrollbar"
    >
      <div
        className={`absolute ${
          overflowMarker === 'right' || overflowMarker === null ? 'hidden' : ''
        } md:hidden left-2 flex justify-start w-14 h-10 bg-gradient-to-r from-white via-white`}
      >
        <div className="flex bg-none justify-center">
          <div className="rotate-90">
            <CaretIcon />
          </div>
        </div>
      </div>

      <div
        className={`absolute ${
          overflowMarker === 'left' || overflowMarker === null ? 'hidden' : ''
        } md:hidden right-2 flex justify-end w-14 h-10 bg-gradient-to-l from-white via-white`}
      >
        <div className="flex bg-none justify-center">
          <div className="-rotate-90">
            <CaretIcon />
          </div>
        </div>
      </div>

      {links.map((link, index) => (
        <div
          key={link.node.uuid}
          data-id={link.node.internalId}
          className={`${buttonStyles} min-w-fit ${
            link.node.internalId === currentStoryInternalId
              ? 'border-gray'
              : 'border-transparent hover:border-black/50 '
          }`}
          ref={(linkElement) => (linkRefs.current[index] = linkElement)}
        >
          {link.node.internalId === currentStoryInternalId ? (
            <span className="px-3 py-1">{linkTitle(link)}</span>
          ) : (
            <Link
              to={getLink(link.node.full_slug)}
              className="flex w-full h-full no-underline"
            >
              <button className="grow px-3 py-1">{linkTitle(link)}</button>
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};

export default PageMenu;
